import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Link } from "react-router-dom";

import { Tooltip } from "@/components/Tooltip";
import type { WidgetComponentProps } from "../types";
import { useInsightsQuery } from "../useInsightsQuery";

function formatSeconds(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AltitudeProfileWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useInsightsQuery();

  const option = useMemo(() => {
    const samples = data?.altitude_profile.samples ?? [];
    const xs = samples.map((s) => s.elapsed_seconds);
    const ys = samples.map((s) => s.altitude_m);
    return {
      grid: { top: 10, right: 12, bottom: 22, left: 40 },
      xAxis: {
        type: "category",
        data: xs,
        axisLabel: {
          color: "#A0AAB8",
          fontSize: 10,
          formatter: (v: number) => formatSeconds(Number(v)),
          interval: Math.max(1, Math.floor(xs.length / 6)),
        },
        axisLine: { lineStyle: { color: "#2A3444" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: "#A0AAB8",
          fontSize: 10,
          formatter: (v: number) => `${v.toFixed(0)}m`,
        },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: "#2A3444", type: "dashed" } },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(10, 14, 20, 0.96)",
        borderColor: "#2A3444",
        textStyle: { color: "#E8ECF1", fontSize: 12 },
        formatter: (params: unknown) => {
          const arr = params as Array<{ value: number; dataIndex: number }>;
          const p = arr[0];
          const sec = xs[p.dataIndex];
          return `<strong>${formatSeconds(Number(sec))}</strong><br/>${p.value.toFixed(0)} m`;
        },
      },
      series: [
        {
          type: "line",
          data: ys,
          smooth: 0.2,
          showSymbol: false,
          lineStyle: { color: "#FBBF24", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(251, 191, 36, 0.3)" },
                { offset: 1, color: "rgba(251, 191, 36, 0.02)" },
              ],
            },
          },
        },
      ],
    };
  }, [data]);

  if (isLoading) return <p className="widget-empty">Loading…</p>;
  const profile = data?.altitude_profile;
  if (!profile || !profile.samples.length) {
    return <p className="widget-empty">No altitude data.</p>;
  }

  return (
    <div className={`altitude-profile altitude-profile--${sizeVariant}`}>
      <div className="altitude-profile__head">
        <div>
          <p className="eyebrow">
            Altitude profile
            <Tooltip
              content={
                <>
                  <strong>Altitude over time</strong>
                  <span>Height vs elapsed seconds for the most recent flight.</span>
                </>
              }
            >
              <span className="info-hint" aria-label="About altitude profile">i</span>
            </Tooltip>
          </p>
          <h3 className="altitude-profile__title">
            {profile.session_name ?? "Last flight"}
          </h3>
        </div>
        <div className="altitude-profile__stats">
          <div>
            <span className="altitude-profile__stat-lbl">MIN</span>
            <span className="altitude-profile__stat-val">
              {profile.min_m?.toFixed(0) ?? "—"} m
            </span>
          </div>
          <div>
            <span className="altitude-profile__stat-lbl">AVG</span>
            <span className="altitude-profile__stat-val">
              {profile.avg_m?.toFixed(0) ?? "—"} m
            </span>
          </div>
          <div>
            <span className="altitude-profile__stat-lbl">MAX</span>
            <span className="altitude-profile__stat-val altitude-profile__stat-val--accent">
              {profile.max_m?.toFixed(0) ?? "—"} m
            </span>
          </div>
        </div>
      </div>
      <div className="altitude-profile__chart">
        <ReactECharts option={option} notMerge style={{ height: "100%", width: "100%" }} />
      </div>
      {profile.flight_id && (
        <Link to={`/flights/${profile.flight_id}`} className="text-link">
          Open replay →
        </Link>
      )}
    </div>
  );
}
